<template>
  <div class="audit-dashboard d-flex flex-column flex-md-row position-relative">
    <!-- Left Sidebar for Filters -->
    <div
      class="ecom-sidebar-header border-r mb-2 mb-md-0 position-relative bg-white"
      :style="{
        flex: sidebarCollapsed ? '0 0 48px' : '0 0 260px',
        maxWidth: sidebarCollapsed ? '48px' : '260px',
        transition: 'width 0.22s ease, flex 0.22s ease, max-width 0.22s ease',
        overflow: 'hidden'
      }"
    >
      <!-- Collapsed view -->
      <div
        v-if="sidebarCollapsed"
        class="d-flex flex-column align-center justify-start pt-3 cursor-pointer fill-height"
        @click="sidebarCollapsed = false"
        style="height: 100%"
      >
        <v-icon size="22" color="primary" class="mb-2">mdi-filter-outline</v-icon>
        <div
          style="
            writing-mode: vertical-rl;
            text-orientation: mixed;
            transform: rotate(180deg);
            font-size: 11px;
            font-weight: 700;
            color: #475569;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            white-space: nowrap;
          "
        >
          Filters
        </div>
      </div>

      <!-- Expanded view -->
      <template v-else>
        <div class="d-flex align-center justify-space-between px-4 pb-2 pt-2 border-b">
          <span
            class="text-subtitle-1 font-weight-bold black--text text-uppercase"
            style="letter-spacing: 1px"
            >FILTERS</span
          >
          <button
            class="text-caption font-weight-bold"
            :class="
              hasActiveFilters
                ? 'cursor-pointer primary--text'
                : 'cursor-default text-slate-400'
            "
            style="letter-spacing: 0.5px; background: none; border: none; padding: 0"
            :disabled="!hasActiveFilters"
            @click="resetFilters"
          >
            CLEAR ALL
          </button>
        </div>

        <div class="pt-2 ecom-sidebar">
          <!-- Search Category -->
          <EcomFilterCategory title="Search" v-model="filters.search" @change="debounceFetch">
            <div class="px-1 py-1">
              <v-text-field
                v-model="filters.search"
                placeholder="Search remarks/metadata..."
                dense
                outlined
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                @input="debounceFetch"
                @click:clear="debounceFetch"
              ></v-text-field>
            </div>
          </EcomFilterCategory>

          <!-- Action Category -->
          <EcomFilterCategory
            title="Action"
            v-model="filters.action"
            :items="actionItems"
            itemText="text"
            itemValue="value"
            @change="fetchAudits(true)"
          />

          <!-- Module Category -->
          <EcomFilterCategory
            title="Module"
            v-model="filters.module"
            :items="moduleItems"
            itemText="text"
            itemValue="value"
            @change="fetchAudits(true)"
          />

          <!-- Performed By Category -->
          <EcomFilterCategory
            title="Performed By"
            v-model="filters.employeeId"
            :items="employeeItems"
            itemText="text"
            itemValue="value"
            :searchable="true"
            @change="fetchAudits(true)"
          />

          <!-- Date Range Category -->
          <EcomFilterCategory title="Date Range">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  class="text-caption mt-1"
                  placeholder="Select Date Range"
                  v-model="formattedDate"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  hide-details
                  outlined
                  dense
                  clearable
                  v-on="on"
                  @click:clear="onDateCleared"
                  background-color="#f5f5f6"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dates"
                @input="onDateSelected"
                no-title
                @change="dateMenu = false"
                range
                scrollable
                color="primary"
              />
            </v-menu>
          </EcomFilterCategory>
        </div>
      </template>
    </div>

    <!-- Sidebar Toggle Button -->
    <div
      style="
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        z-index: 10;
        pointer-events: none;
        display: flex;
        align-items: center;
      "
      :style="{ left: sidebarCollapsed ? '48px' : '260px', transition: 'left 0.22s ease' }"
    >
      <button
        @click="sidebarCollapsed = !sidebarCollapsed"
        style="
          pointer-events: all;
          width: 18px;
          height: 48px;
          background: #cbd5e1;
          border: 1px solid #94a3b8;
          border-left: none;
          border-radius: 0 6px 6px 0;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 2px 0 6px rgba(0, 0, 0, 0.12);
          padding: 0;
          outline: none;
          transition: background 0.15s;
        "
        @mouseover="$event.currentTarget.style.background = '#94a3b8'"
        @mouseleave="$event.currentTarget.style.background = '#cbd5e1'"
      >
        <v-icon size="13" color="grey darken-3">
          {{ sidebarCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}
        </v-icon>
      </button>
    </div>

    <!-- Main Content / Table Area -->
    <div class="ecom-table-container flex-grow-1 pl-md-4">
      <v-card flat class="border-light">
        <v-data-table
          :headers="loanHeaders"
          :items="auditLogs"
          :options.sync="options"
          :server-items-length="totalItems"
          :loading="loading"
          loading-text="Loading audit logs..."
          class="custom-audit-table"
          :footer-props="{
            'items-per-page-options': [10, 25, 50],
            'show-first-last-page': true,
            'items-per-page-text': 'Rows per page'
          }"
        >
          <!-- Loan No Column -->
          <template v-slot:[`item.loanNo`]="{ item }">
            <div class="px-2 font-weight-bold grey--text text--darken-4">
              {{ item.loanId?.applicationNumber || '—' }}
            </div>
          </template>
          <!-- Module Column -->
          <template v-slot:[`item.module`]="{ item }">
            <div class="px-2">
              <v-chip x-small label class="font-weight-bold text-uppercase" color="grey lighten-3">
                {{ item.module }}
              </v-chip>
            </div>
          </template>

          <!-- Action Column -->
          <template v-slot:[`item.action`]="{ item }">
            <div class="d-flex align-center gap-2 px-2">
              <v-chip
                small
                label
                :color="getActionColor(item.action)"
                dark
                class="font-weight-bold text-uppercase"
              >
                {{ item.action }}
              </v-chip>
            </div>
          </template>

          <!-- User Column -->
          <template v-slot:[`item.whoChanged`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="26" color="primary lighten-4" class="mr-2">
                <span class="primary--text text--darken-2 text-caption font-weight-black">
                  {{ (item.whoChanged?.firstName || 'S')[0].toUpperCase() }}
                </span>
              </v-avatar>
              <span class="text-body-2 font-weight-medium">
                {{
                  item.whoChanged
                    ? `${item.whoChanged.firstName} ${item.whoChanged.lastName || ''}`.trim()
                    : 'System'
                }}
              </span>
            </div>
          </template>

          <!-- Timestamp Column -->
          <template v-slot:[`item.createdAt`]="{ item }">
            <span class="text-body-2 grey--text text--darken-2">{{
              formatDate(item.createdAt)
            }}</span>
          </template>

          <!-- View Details Column -->
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              x-small
              depressed
              color="primary lighten-5 primary--text"
              class="font-weight-bold"
              @click="showDetails(item)"
            >
              <v-icon x-small left>mdi-eye-outline</v-icon>
              View
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="900px" scrollable>
      <v-card v-if="selectedLog" class="rounded-lg overflow-hidden">
        <!-- Header -->
        <div
          class="pa-4 border-b d-flex align-center justify-space-between"
          style="background: #f8f9fa"
        >
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-file-document-edit-outline</v-icon>
            <div>
              <div class="text-subtitle-1 font-weight-black grey--text text--darken-4">
                Audit Log Detail
              </div>
              <div class="text-caption grey--text">
                {{ selectedLog.module }} &bull; {{ formatDate(selectedLog.createdAt) }}
              </div>
            </div>
          </div>
          <v-btn icon small @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-4" style="max-height: 75vh; overflow-y: auto">
          <!-- Meta row -->
          <div
            class="d-flex flex-wrap gap-4 mb-5 pa-3 rounded"
            style="background: #f0f4ff; border: 1px solid #c7d4f7"
          >
            <div>
              <div class="text-caption grey--text font-weight-bold text-uppercase">Action</div>
              <v-chip
                small
                label
                dark
                :color="getActionColor(selectedLog.action)"
                class="font-weight-bold mt-1"
              >
                {{ selectedLog.action }}
              </v-chip>
            </div>
            <div class="border-l pl-4">
              <div class="text-caption grey--text font-weight-bold text-uppercase">Module</div>
              <div class="text-body-2 font-weight-black mt-1">{{ selectedLog.module }}</div>
            </div>
            <div class="border-l pl-4">
              <div class="text-caption grey--text font-weight-bold text-uppercase">
                Performed By
              </div>
              <div class="text-body-2 font-weight-black mt-1">
                {{
                  selectedLog.whoChanged
                    ? `${selectedLog.whoChanged.firstName} ${
                        selectedLog.whoChanged.lastName || ''
                      }`.trim()
                    : 'System'
                }}
              </div>
            </div>
            <div class="border-l pl-4">
              <div class="text-caption grey--text font-weight-bold text-uppercase">
                Date &amp; Time
              </div>
              <div class="text-body-2 font-weight-black mt-1">
                {{ formatDate(selectedLog.createdAt) }}
              </div>
            </div>
          </div>

          <!-- Emi Payments / Late Fee Receipt Details Breakdown -->
          <div
            v-if="selectedLog.module === 'EmiManagement' && getLatestPayment(selectedLog)"
            class="mb-6 border rounded-lg overflow-hidden"
          >
            <div
              class="border-b pa-3 d-flex align-center justify-space-between"
              :style="
                getLatestPayment(selectedLog).isDeleted
                  ? 'background-color: #fef2f2; border-bottom: 1px solid #fca5a5;'
                  : 'background-color: #ecfdf5; border-bottom: 1px solid #a7f3d0;'
              "
            >
              <div class="d-flex align-center">
                <v-icon
                  :color="getLatestPayment(selectedLog).isDeleted ? 'error' : 'success'"
                  class="mr-2"
                >
                  {{
                    getLatestPayment(selectedLog).isDeleted ? 'mdi-receipt-cancel' : 'mdi-receipt'
                  }}
                </v-icon>
                <span
                  class="text-subtitle-2 font-weight-black"
                  :style="
                    getLatestPayment(selectedLog).isDeleted ? 'color: #991b1b;' : 'color: #065f46;'
                  "
                >
                  Payment &amp; Fees Receipt Details
                </span>
              </div>
              <v-chip
                v-if="getLatestPayment(selectedLog).isDeleted"
                small
                color="error"
                label
                class="font-weight-black text-uppercase animate-pulse"
                dark
              >
                Reverted Payment
              </v-chip>
            </div>
            <div class="pa-4 bg-white">
              <v-row dense>
                <v-col cols="12" sm="6" md="4" class="mb-2">
                  <div class="text-caption grey--text font-weight-bold text-uppercase">
                    Total Amount Received
                  </div>
                  <div class="text-h6 font-weight-black green--text" style="color: #10b981">
                    {{
                      formatCurrency(
                        Number(getLatestPayment(selectedLog).amount || 0) +
                          Number(
                            getLatestPayment(selectedLog).latePaymentAmount ||
                              getLatestPayment(selectedLog).penaltyAmount ||
                              0
                          )
                      )
                    }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4" class="mb-2">
                  <div class="text-caption grey--text font-weight-bold text-uppercase">
                    Late Payment Charges (LPC)
                  </div>
                  <div class="text-body-1 font-weight-black red--text" style="color: #ef4444">
                    {{
                      formatCurrency(
                        getLatestPayment(selectedLog).latePaymentAmount ||
                          getLatestPayment(selectedLog).penaltyAmount ||
                          0
                      )
                    }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4" class="mb-2">
                  <div class="text-caption grey--text font-weight-bold text-uppercase">
                    Principal Amount
                  </div>
                  <div class="text-body-1 font-weight-bold grey--text text--darken-4">
                    {{ formatCurrency(getLatestPayment(selectedLog).principalAmount) }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4" class="mb-2">
                  <div class="text-caption grey--text font-weight-bold text-uppercase">
                    Interest Amount
                  </div>
                  <div class="text-body-1 font-weight-bold grey--text text--darken-4">
                    {{ formatCurrency(getLatestPayment(selectedLog).interestOutStandingAmount) }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4" class="mb-2">
                  <div class="text-caption grey--text font-weight-bold text-uppercase">
                    Payment Mode
                  </div>
                  <v-chip
                    small
                    color="primary"
                    label
                    class="font-weight-black text-uppercase mt-1"
                    dark
                  >
                    {{ getLatestPayment(selectedLog).paymentMode || '—' }}
                  </v-chip>
                </v-col>
                <v-col cols="12" sm="6" md="4" class="mb-2">
                  <div class="text-caption grey--text font-weight-bold text-uppercase">
                    Voucher Number
                  </div>
                  <div class="text-body-2 font-weight-black font-mono mt-1">
                    {{ getLatestPayment(selectedLog).voucherNo || '—' }}
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>

          <!-- UPDATE / DELETE: Before vs After comparison -->
          <template v-if="selectedLog.action === 'UPDATE' || selectedLog.action === 'DELETE'">
            <div class="text-subtitle-2 font-weight-black mb-3 d-flex align-center">
              <v-icon small class="mr-1" color="info">mdi-swap-horizontal</v-icon>
              Changes (Before → After)
            </div>
            <div
              v-if="parsedChanges.length === 0"
              class="text-center pa-6 grey lighten-5 rounded border"
            >
              <v-icon color="grey lighten-1" size="36">mdi-check-circle-outline</v-icon>
              <div class="text-body-2 grey--text mt-2">No field-level differences detected.</div>
            </div>
            <div v-else class="border rounded overflow-hidden">
              <table class="w-full change-table text-sm">
                <thead>
                  <tr class="grey lighten-4">
                    <th class="pa-3 text-left font-weight-bold border-r" style="width: 30%">
                      Field
                    </th>
                    <th class="pa-3 text-left font-weight-bold border-r" style="width: 35%">
                      <div class="d-flex align-center">
                        <v-icon x-small class="mr-1" color="red darken-1"
                          >mdi-arrow-left-circle</v-icon
                        >
                        Before
                      </div>
                    </th>
                    <th class="pa-3 text-left font-weight-bold" style="width: 35%">
                      <div class="d-flex align-center">
                        <v-icon x-small class="mr-1" color="green darken-1"
                          >mdi-arrow-right-circle</v-icon
                        >
                        After
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="change in parsedChanges" :key="change.key" class="border-t change-row">
                    <td class="pa-3 border-r">
                      <span
                        class="text-caption font-weight-black grey--text text--darken-3 text-uppercase"
                      >
                        {{ formatFieldName(change.key) }}
                      </span>
                    </td>
                    <td class="pa-2 border-r">
                      <div class="diff-box old">{{ formatDisplayValue(change.from) }}</div>
                    </td>
                    <td class="pa-2">
                      <div class="diff-box new">{{ formatDisplayValue(change.to) }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <!-- CREATE / DELETE: Snapshot view -->
          <template v-else>
            <div class="text-subtitle-2 font-weight-black mb-3 d-flex align-center">
              <v-icon
                small
                class="mr-1"
                :color="selectedLog.action === 'CREATE' ? 'success' : 'error'"
              >
                {{
                  selectedLog.action === 'CREATE'
                    ? 'mdi-plus-circle-outline'
                    : 'mdi-minus-circle-outline'
                }}
              </v-icon>
              {{
                selectedLog.action === 'CREATE'
                  ? 'Created Record Snapshot'
                  : 'Deleted Record Snapshot'
              }}
            </div>
            <div
              v-if="snapshotData.length === 0"
              class="text-center pa-6 grey lighten-5 rounded border"
            >
              <div class="text-body-2 grey--text">No snapshot data available.</div>
            </div>
            <v-row v-else dense class="border rounded ma-0">
              <v-col
                v-for="item in snapshotData"
                :key="item.key"
                cols="12"
                sm="6"
                md="4"
                class="pa-3 border-b border-r snapshot-cell"
              >
                <div class="text-caption grey--text font-weight-bold text-uppercase">
                  {{ formatFieldName(item.key) }}
                </div>
                <div class="text-body-2 font-weight-medium mt-1">
                  {{ formatDisplayValue(item.value) }}
                </div>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { axiosUrl } from '@/utils/workforceApi'
import EcomFilterCategory from '@/components/EcomFilterCategory.vue'

const IGNORED_FIELDS = new Set([
  '_id',
  '__v',
  'updatedAt',
  'createdAt',
  'createdBy',
  'updatedBy',
  'loanDisbursementsData',
  'loanWorkflowData',
  'emiWorkflowData',
  'history'
])

export default {
  name: 'AuditTable',
  components: {
    EcomFilterCategory
  },
  props: {
    caseConfig: Object,
    loanId: { type: String, default: '' },
    caseId: { type: String, default: '' },
    subCaseId: { type: String, default: '' }
  },
  data() {
    return {
      sidebarCollapsed: false,
      dateMenu: false,
      dates: [],
      formattedDate: '',
      loading: false,
      auditLogs: [],
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      loanHeaders: [
        { text: 'Loan No', value: 'loanNo', sortable: false },
        { text: 'Module', value: 'module', sortable: false },
        { text: 'Action', value: 'action', sortable: false },
        { text: 'Performed By', value: 'whoChanged', sortable: false },
        { text: 'Date & Time', value: 'createdAt', sortable: true },
        { text: '', value: 'actions', sortable: false, align: 'center' }
      ],
      filters: {
        search: '',
        action: '',
        module: '',
        employeeId: '',
        start: '',
        end: ''
      },
      actionItems: [
        { text: 'Create', value: 'CREATE' },
        { text: 'Update', value: 'UPDATE' },
        { text: 'Delete', value: 'DELETE' }
      ],
      moduleItems: [
        { text: 'Case', value: 'Case' },
        { text: 'Loan Management', value: 'LoanManagement' },
        { text: 'EMI Management', value: 'EmiManagement' }
      ],
      employeeItems: [],
      // Details dialog
      detailsDialog: false,
      selectedLog: null,
      debounceTimer: null
    }
  },
  computed: {
    hasActiveFilters() {
      const f = this.filters || {}
      const hasSearch = !!(f.search && f.search.trim())
      const hasAction = !!f.action
      const hasModule = !!f.module
      const hasEmp = !!f.employeeId
      const hasDate = (Array.isArray(this.dates) && this.dates.length > 0) || !!f.start || !!f.end
      return !!(hasSearch || hasAction || hasModule || hasEmp || hasDate)
    },
    parsedChanges() {
      const item = this.selectedLog
      if (!item || (item.action !== 'UPDATE' && item.action !== 'DELETE')) return []

      const fromObj = item.changes?.before || {}
      const toObj = item.changes?.after || {}

      if (typeof fromObj !== 'object' || typeof toObj !== 'object') return []

      return Array.from(new Set([...Object.keys(fromObj), ...Object.keys(toObj)]))
        .filter((k) => !k.startsWith('_') && !IGNORED_FIELDS.has(k))
        .map((k) => ({ key: k, from: fromObj[k], to: toObj[k] }))
        .filter((c) => {
          const f = c.from == null ? '' : JSON.stringify(c.from)
          const t = c.to == null ? '' : JSON.stringify(c.to)
          return f !== t
        })
    },
    snapshotData() {
      const item = this.selectedLog
      if (!item) return []
      const snap = item.changes?.after || item.changes?.before || {}
      if (typeof snap !== 'object') return []
      return Object.keys(snap)
        .filter((k) => !IGNORED_FIELDS.has(k) && snap[k] != null)
        .map((k) => ({ key: k, value: snap[k] }))
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchAudits()
      },
      deep: true
    },
    '$route.params.subCase': {
      handler() {
        this.options.page = 1
        this.fetchAudits()
      }
    }
  },
  mounted() {
    this.fetchEmployees()
    this.fetchAudits()
  },
  methods: {
    async fetchAudits(resetPage = false) {
      if (resetPage) {
        this.options.page = 1
      }
      this.loading = true
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options
        const currentSubCase = this.subCaseId || this.$route.params.subCase || undefined
        const currentLoanId = this.loanId || undefined
        const currentCaseId = this.caseId || undefined

        const params = {
          page,
          limit: itemsPerPage,
          sortBy: sortBy?.[0] || 'createdAt',
          sortOrder: sortDesc?.[0] ? 'desc' : 'asc',
          subCase: currentSubCase,
          loanId: currentLoanId,
          caseId: currentCaseId,
          action: this.filters.action || undefined,
          module: this.filters.module || undefined,
          employeeId: this.filters.employeeId || undefined,
          start: this.filters.start ? new Date(this.filters.start).toISOString() : undefined,
          end: this.filters.end
            ? new Date(this.filters.end + 'T23:59:59').toISOString()
            : undefined,
          search: this.filters.search || undefined
        }

        const response = await this.$axios.get(axiosUrl.cases.getAuditLogs, { params })
        const responseData = response.data

        this.auditLogs = responseData?.data || []
        this.totalItems =
          responseData?.pagination?.totalCount ?? responseData?.total ?? this.auditLogs.length
      } catch (error) {
        console.error('Audit fetch error:', error)
      } finally {
        this.loading = false
      }
    },
    debounceFetch() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.fetchAudits(true)
      }, 400)
    },
    onDateSelected() {
      if (this.dates && this.dates.length === 2) {
        const sorted = [...this.dates].sort()
        this.filters.start = sorted[0]
        this.filters.end = sorted[1]
        this.formattedDate = `${this.$dayjs(sorted[0]).format('DD/MM/YYYY')} - ${this.$dayjs(
          sorted[1]
        ).format('DD/MM/YYYY')}`
        this.fetchAudits(true)
      } else if (this.dates && this.dates.length === 1) {
        this.filters.start = this.dates[0]
        this.filters.end = ''
        this.formattedDate = this.$dayjs(this.dates[0]).format('DD/MM/YYYY')
      }
    },
    onDateCleared() {
      this.dates = []
      this.filters.start = ''
      this.filters.end = ''
      this.formattedDate = ''
      this.fetchAudits(true)
    },
    resetFilters() {
      this.filters = {
        search: '',
        action: '',
        module: '',
        employeeId: '',
        start: '',
        end: ''
      }
      this.dates = []
      this.formattedDate = ''
      this.fetchAudits(true)
    },
    async fetchEmployees() {
      try {
        const response = await this.$axios.get('/v3/users', {
          params: {
            limit: '*',
            sortBy: 'firstName',
            sortOrder: 'asc'
          }
        })
        const users = response.data.users || response.data.data || []
        this.employeeItems = users.map((u) => ({
          text: u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
          value: u._id
        }))
      } catch (err) {
        console.error('Error fetching employees for filter:', err)
      }
    },

    getLatestPayment(log) {
      const emiPaymentsAfter = log?.changes?.after?.emiPayments || []
      const emiPaymentsBefore = log?.changes?.before?.emiPayments || []
      // Find the new payment that wasn't in before
      const newPayments = emiPaymentsAfter.filter(
        (p) => !emiPaymentsBefore.some((bp) => bp._id === p._id)
      )
      if (newPayments.length > 0) return newPayments[newPayments.length - 1]
      if (emiPaymentsAfter.length > 0) return emiPaymentsAfter[emiPaymentsAfter.length - 1]
      return null
    },

    formatCurrency(val) {
      if (val == null) return '—'
      return Number(val).toLocaleString('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2
      })
    },

    showDetails(item) {
      this.selectedLog = item
      this.detailsDialog = true
    },

    getActionColor(action) {
      return { CREATE: 'success', UPDATE: 'info', DELETE: 'error' }[action] || 'grey'
    },

    formatDate(date) {
      if (!date) return '-'
      return this.$dayjs(date).format('DD MMM YYYY, hh:mm A')
    },

    formatFieldName(field) {
      if (!field) return '-'
      return field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim()
    },

    formatDisplayValue(val) {
      if (val === null || val === undefined) return '—'
      if (typeof val === 'boolean') return val ? 'Yes' : 'No'
      if (val instanceof Date || (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val))) {
        return this.$dayjs(val).format('DD MMM YYYY, hh:mm A')
      }
      if (typeof val === 'object') {
        if (Array.isArray(val)) return `${val.length} item${val.length !== 1 ? 's' : ''}`
        return (
          val.name || val.fullName || val.firstName || val.label || JSON.stringify(val).slice(0, 60)
        )
      }
      return String(val)
    }
  }
}
</script>

<style scoped>
.custom-audit-table >>> th {
  background-color: #f5f5f5 !important;
  color: #555 !important;
  font-weight: 700 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.border-light {
  border: 1px solid #e0e0e0 !important;
}

.border {
  border: 1px solid #e0e0e0 !important;
}

.border-b {
  border-bottom: 1px solid #e0e0e0 !important;
}

.border-l {
  border-left: 1px solid #e0e0e0 !important;
}

.border-r {
  border-right: 1px solid #e0e0e0 !important;
}

.border-t {
  border-top: 1px solid #e0e0e0 !important;
}

/* Change comparison table */
.change-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.change-row:hover {
  background-color: #fafbff;
}

/* Diff boxes */
.diff-box {
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 0.82rem;
  line-height: 1.5;
  word-break: break-word;
  min-height: 28px;
}

.diff-box.old {
  background-color: #fff1f0;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.diff-box.new {
  background-color: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
  font-weight: 600;
}

/* Snapshot grid */
.snapshot-cell {
  border-bottom: 1px solid #eeeeee !important;
  border-right: 1px solid #eeeeee !important;
}

.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}

.w-full {
  width: 100%;
}

.text-sm {
  font-size: 0.875rem;
}

.ecom-sidebar-header {
  border-right: 1px solid #e9e9ed;
  flex: 0 0 260px;
  max-width: 260px;
}
.ecom-sidebar {
  height: calc(100vh - 220px);
  overflow-y: auto;
}
.ecom-table-container {
  height: calc(100vh - 220px);
  overflow-y: auto;
  overflow-x: auto;
  min-width: 0;
}
.cursor-pointer {
  cursor: pointer;
}

@media (max-width: 959px) {
  .ecom-sidebar {
    flex: 0 0 100%;
    max-width: 100%;
    height: 35vh;
    border-right: none !important;
    border-bottom: 1px solid #e9e9ed;
  }
  .ecom-table-container {
    height: 50vh;
    padding-left: 0 !important;
  }
}
</style>
